// web/src/scene/Lighting.tsx
// M1.4 port of webview/src/scene/Lighting.tsx.
// Replaces eventBus.on('CAMERA_FIT_TO_MODEL') with a modelBounds prop from MineScene.
import { useEffect, useRef } from 'react'
import * as THREE from 'three'

interface ModelBounds {
  center: { x: number; y: number; z: number }
  /** Largest axis extent of the model bounding box. */
  size: number
}

interface LightingProps {
  performanceMode?: boolean
  enableShadows?: boolean
  modelBounds?: ModelBounds | null
}

export function Lighting({
  performanceMode = false,
  enableShadows = false,
  modelBounds = null,
}: LightingProps) {
  const mainLightRef = useRef<THREE.DirectionalLight>(null)
  const fillLightRef = useRef<THREE.DirectionalLight>(null)

  // Reposition lights when model bounds change (prop-driven, no eventBus)
  useEffect(() => {
    if (!modelBounds) return

    const { center, size } = modelBounds
    const offset = size * 0.8
    const shadowExtent = size * 1.2

    // Z-up: elevate lights on Z axis (vertical), offset on X/Y (horizontal)
    if (mainLightRef.current) {
      mainLightRef.current.position.set(
        center.x + offset * 0.4,
        center.y + offset * 0.4,
        center.z + offset,
      )
      mainLightRef.current.target.position.set(center.x, center.y, center.z)
      mainLightRef.current.target.updateMatrixWorld()

      if (mainLightRef.current.shadow?.camera) {
        const cam = mainLightRef.current.shadow.camera as THREE.OrthographicCamera
        cam.near = 1
        cam.far = size * 4
        cam.left = -shadowExtent
        cam.right = shadowExtent
        cam.top = shadowExtent
        cam.bottom = -shadowExtent
        cam.updateProjectionMatrix()
      }
    }

    if (fillLightRef.current) {
      fillLightRef.current.position.set(
        center.x - offset * 0.3,
        center.y - offset * 0.3,
        center.z + offset * 0.6,
      )
      fillLightRef.current.target.position.set(center.x, center.y, center.z)
      fillLightRef.current.target.updateMatrixWorld()
    }
  }, [modelBounds])

  return (
    <>
      <ambientLight intensity={enableShadows ? 1.5 : 2.0} color="#ffffff" />
      <directionalLight
        ref={mainLightRef}
        position={[9060, 6049, 5500]}
        intensity={1.5}
        color="#ffffff"
        castShadow={enableShadows && !performanceMode}
        shadow-mapSize-width={performanceMode ? 512 : 2048}
        shadow-mapSize-height={performanceMode ? 512 : 2048}
        shadow-camera-near={1}
        shadow-camera-far={5000}
        shadow-camera-left={-1500}
        shadow-camera-right={1500}
        shadow-camera-top={1500}
        shadow-camera-bottom={-1500}
      />
      <directionalLight
        ref={fillLightRef}
        position={[9060, 6049, 5000]}
        intensity={0.6}
        color="#aabbcc"
      />
      {!performanceMode && (
        <hemisphereLight args={['#87ceeb', '#362d1b', 0.8]} />
      )}
    </>
  )
}
